import { supabase } from '../lib/supabaseClient'
import type { Database } from '../lib/supabaseClient'

type Tables = Database['public']['Tables']
type Profile = Tables['profiles']['Row']
type ProfileInsert = Tables['profiles']['Insert']
type ProfileUpdate = Tables['profiles']['Update']
type Aula = Tables['aulas']['Row']
type AulaInsert = Tables['aulas']['Insert']
type AulaUpdate = Tables['aulas']['Update']
type Teste = Tables['testes']['Row']
type TesteInsert = Tables['testes']['Insert']
type TesteUpdate = Tables['testes']['Update']
type Questao = Tables['questoes']['Row']
type QuestaoInsert = Tables['questoes']['Insert']
type QuestaoUpdate = Tables['questoes']['Update']
type Alternativa = Tables['alternativas']['Row']
type AlternativaInsert = Tables['alternativas']['Insert']
type AlternativaUpdate = Tables['alternativas']['Update']
type Resultado = Tables['resultados']['Row']
type ResultadoInsert = Tables['resultados']['Insert']
type ResultadoUpdate = Tables['resultados']['Update']
type Resposta = Tables['respostas']['Row']
type RespostaInsert = Tables['respostas']['Insert']
type RespostaUpdate = Tables['respostas']['Update']
type Noticia = Tables['noticias']['Row']
type NoticiaInsert = Tables['noticias']['Insert']
type NoticiaUpdate = Tables['noticias']['Update']

// ============================================================================
// PROFILES (USUÁRIOS)
// ============================================================================

// Helper para timeout que aceita Promise ou PromiseLike (como PostgrestBuilder)
const withTimeout = <T>(promise: PromiseLike<T>, ms: number = 8000, errorMsg: string = 'Timeout na operação'): Promise<T> => {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(errorMsg)), ms)
    Promise.resolve(promise)
      .then((res) => {
        clearTimeout(timer)
        resolve(res)
      })
      .catch((err) => {
        clearTimeout(timer)
        reject(err)
      })
  })
}

export const profilesService = {
  // Listar todos os perfis (apenas admins)
  async getAll(): Promise<Profile[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  },

  // Buscar perfil por ID
  async getById(id: string): Promise<Profile | null> {
    try {
      // Adicionando timeout na busca de perfil pois é crítica para o login
      // Usamos any cast para evitar problemas complexos de tipagem com PostgrestBuilder
      const result = await withTimeout(
        supabase
          .from('profiles')
          .select('*')
          .eq('id', id)
          .single() as PromiseLike<any>,
        15000, // Increased from 8000 to prevent timeout issues
        'Timeout ao buscar perfil do usuário'
      )

      const { data, error } = result

      if (error) {
        if (error.code === 'PGRST116') return null // Not found
        throw error
      }
      return data
    } catch (error) {
      console.error('Erro em getById:', error)
      // Se for timeout, propagamos o erro para ser tratado no nível superior
      throw error
    }
  },

  // Criar novo perfil
  async create(profile: ProfileInsert): Promise<Profile> {
    const { data, error } = await supabase
      .from('profiles')
      .insert(profile as any)
      .select()
      .single()

    if (error) throw error
    return data as Profile
  },

  // Atualizar perfil
  async update(id: string, updates: ProfileUpdate): Promise<Profile> {
    const { data, error } = await (supabase as any)
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Profile
  },

  // Deletar perfil
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('profiles')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // Buscar perfis por role
  async getByRole(role: 'admin' | 'professor' | 'aluno'): Promise<Profile[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', role)
      .order('full_name', { ascending: true })

    if (error) throw error
    return data || []
  }
}

// ============================================================================
// AULAS
// ============================================================================

export const aulasService = {
  // Listar todas as aulas
  async getAll(): Promise<Aula[]> {
    const { data, error } = await supabase
      .from('aulas')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  },

  // Buscar aula por ID
  async getById(id: string): Promise<Aula | null> {
    const { data, error } = await supabase
      .from('aulas')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw error
    }
    return data
  },

  // Criar nova aula
  async create(aula: AulaInsert): Promise<Aula> {
    const { data, error } = await supabase
      .from('aulas')
      .insert(aula as any)
      .select()
      .single()

    if (error) throw error
    return data as Aula
  },

  // Atualizar aula
  async update(id: string, updates: AulaUpdate): Promise<Aula> {
    const { data, error } = await (supabase as any)
      .from('aulas')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Aula
  },

  // Deletar aula
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('aulas')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // Buscar aulas por criador
  async getByCreator(createdBy: string): Promise<Aula[]> {
    const { data, error } = await supabase
      .from('aulas')
      .select('*')
      .eq('created_by', createdBy)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  }
}

// ============================================================================
// TESTES
// ============================================================================

export const testesService = {
  // Listar todos os testes
  async getAll(): Promise<Teste[]> {
    const { data, error } = await supabase
      .from('testes')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  },

  // Buscar teste por ID
  async getById(id: string): Promise<Teste | null> {
    const { data, error } = await supabase
      .from('testes')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw error
    }
    return data
  },

  // Criar novo teste
  async create(teste: TesteInsert): Promise<Teste> {
    const { data, error } = await supabase
      .from('testes')
      .insert(teste as any)
      .select()
      .single()

    if (error) throw error
    return data as Teste
  },

  // Atualizar teste
  async update(id: string, updates: TesteUpdate): Promise<Teste> {
    const { data, error } = await (supabase as any)
      .from('testes')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Teste
  },

  // Deletar teste
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('testes')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // Buscar testes por aula
  async getByAula(aulaId: string): Promise<Teste[]> {
    const { data, error } = await supabase
      .from('testes')
      .select('*')
      .eq('aula_id', aulaId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  },

  // Buscar testes publicados
  async getPublished(): Promise<Teste[]> {
    const { data, error } = await supabase
      .from('testes')
      .select('*')
      .eq('is_published', true)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  }
}

// ============================================================================
// QUESTÕES
// ============================================================================

export const questoesService = {
  // Listar questões por teste
  async getByTeste(testeId: string): Promise<Questao[]> {
    const { data, error } = await supabase
      .from('questoes')
      .select('*')
      .eq('teste_id', testeId)
      .order('ordem', { ascending: true })

    if (error) throw error
    return data || []
  },

  // Buscar questão por ID
  async getById(id: string): Promise<Questao | null> {
    const { data, error } = await supabase
      .from('questoes')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw error
    }
    return data
  },

  // Criar nova questão
  async create(questao: QuestaoInsert): Promise<Questao> {
    const { data, error } = await supabase
      .from('questoes')
      .insert(questao as any)
      .select()
      .single()

    if (error) throw error
    return data as Questao
  },

  // Atualizar questão
  async update(id: string, updates: QuestaoUpdate): Promise<Questao> {
    const { data, error } = await (supabase as any)
      .from('questoes')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Questao
  },

  // Deletar questão
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('questoes')
      .delete()
      .eq('id', id)

    if (error) throw error
  }
}

// ============================================================================
// ALTERNATIVAS
// ============================================================================

export const alternativasService = {
  // Listar alternativas por questão
  async getByQuestao(questaoId: string): Promise<Alternativa[]> {
    const { data, error } = await supabase
      .from('alternativas')
      .select('*')
      .eq('questao_id', questaoId)
      .order('created_at', { ascending: true })

    if (error) throw error
    return data || []
  },

  // Buscar alternativa por ID
  async getById(id: string): Promise<Alternativa | null> {
    const { data, error } = await supabase
      .from('alternativas')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw error
    }
    return data
  },

  // Criar nova alternativa
  async create(alternativa: AlternativaInsert): Promise<Alternativa> {
    const { data, error } = await supabase
      .from('alternativas')
      .insert(alternativa as any)
      .select()
      .single()

    if (error) throw error
    return data as Alternativa
  },

  // Atualizar alternativa
  async update(id: string, updates: AlternativaUpdate): Promise<Alternativa> {
    const { data, error } = await (supabase as any)
      .from('alternativas')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Alternativa
  },

  // Deletar alternativa
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('alternativas')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // Criar múltiplas alternativas para uma questão
  async createMultiple(alternativas: AlternativaInsert[]): Promise<Alternativa[]> {
    const { data, error } = await (supabase as any)
      .from('alternativas')
      .insert(alternativas)
      .select()

    if (error) throw error
    return data as Alternativa[]
  }
}

// ============================================================================
// RESULTADOS
// ============================================================================

export const resultadosService = {
  // Listar resultados por aluno
  async getByAluno(alunoId: string): Promise<Resultado[]> {
    const { data, error } = await supabase
      .from('resultados')
      .select('*')
      .eq('aluno_id', alunoId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  },

  // Listar resultados por teste
  async getByTeste(testeId: string): Promise<Resultado[]> {
    const { data, error } = await supabase
      .from('resultados')
      .select('*')
      .eq('teste_id', testeId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  },

  // Buscar resultado por ID
  async getById(id: string): Promise<Resultado | null> {
    const { data, error } = await supabase
      .from('resultados')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw error
    }
    return data
  },

  // Criar novo resultado
  async create(resultado: ResultadoInsert): Promise<Resultado> {
    const { data, error } = await (supabase as any)
      .from('resultados')
      .insert(resultado)
      .select()
      .single()

    if (error) throw error
    return data as Resultado
  },

  // Atualizar resultado
  async update(id: string, updates: ResultadoUpdate): Promise<Resultado> {
    const { data, error } = await (supabase as any)
      .from('resultados')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Resultado
  },

  // Deletar resultado
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('resultados')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // Buscar resultado específico de aluno em teste
  async getByAlunoAndTeste(alunoId: string, testeId: string): Promise<Resultado | null> {
    const { data, error } = await supabase
      .from('resultados')
      .select('*')
      .eq('aluno_id', alunoId)
      .eq('teste_id', testeId)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw error
    }
    return data
  }
}

// ============================================================================
// RESPOSTAS
// ============================================================================

export const respostasService = {
  // Listar respostas por resultado
  async getByResultado(resultadoId: string): Promise<Resposta[]> {
    const { data, error } = await supabase
      .from('respostas')
      .select('*')
      .eq('resultado_id', resultadoId)
      .order('created_at', { ascending: true })

    if (error) throw error
    return data || []
  },

  // Buscar resposta por ID
  async getById(id: string): Promise<Resposta | null> {
    const { data, error } = await supabase
      .from('respostas')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw error
    }
    return data
  },

  // Criar nova resposta
  async create(resposta: RespostaInsert): Promise<Resposta> {
    const { data, error } = await (supabase as any)
      .from('respostas')
      .insert(resposta)
      .select()
      .single()

    if (error) throw error
    return data as Resposta
  },

  // Atualizar resposta
  async update(id: string, updates: RespostaUpdate): Promise<Resposta> {
    const { data, error } = await (supabase as any)
      .from('respostas')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Resposta
  },

  // Deletar resposta
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('respostas')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // Criar múltiplas respostas
  async createMultiple(respostas: RespostaInsert[]): Promise<Resposta[]> {
    const { data, error } = await (supabase as any)
      .from('respostas')
      .insert(respostas)
      .select()

    if (error) throw error
    return data as Resposta[]
  }
}

// ============================================================================
// NOTÍCIAS
// ============================================================================

export const noticiasService = {
  // Listar todas as notícias
  async getAll(): Promise<Noticia[]> {
    const { data, error } = await supabase
      .from('noticias')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  },

  // Buscar notícia por ID
  async getById(id: string): Promise<Noticia | null> {
    const { data, error } = await supabase
      .from('noticias')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw error
    }
    return data
  },

  // Criar nova notícia
  async create(noticia: NoticiaInsert): Promise<Noticia> {
    const { data, error } = await (supabase as any)
      .from('noticias')
      .insert(noticia)
      .select()
      .single()

    if (error) throw error
    return data as Noticia
  },

  // Atualizar notícia
  async update(id: string, updates: NoticiaUpdate): Promise<Noticia> {
    const { data, error } = await (supabase as any)
      .from('noticias')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Noticia
  },

  // Deletar notícia
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('noticias')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // Buscar notícias em destaque
  async getFeatured(): Promise<Noticia[]> {
    const { data, error } = await supabase
      .from('noticias')
      .select('*')
      .eq('featured', true)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  },

  // Buscar notícias por criador
  async getByCreator(createdBy: string): Promise<Noticia[]> {
    const { data, error } = await supabase
      .from('noticias')
      .select('*')
      .eq('created_by', createdBy)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  }
}

// ============================================================================
// SERVIÇOS COMPOSTOS
// ============================================================================

// Buscar questão completa com alternativas
export const getQuestaoCompleta = async (questaoId: string) => {
  const questao = await questoesService.getById(questaoId)
  if (!questao) return null

  const alternativas = await alternativasService.getByQuestao(questaoId)

  return {
    ...questao,
    alternativas
  }
}

// Buscar teste completo com questões e alternativas
export const getTesteCompleto = async (testeId: string) => {
  const teste = await testesService.getById(testeId)
  if (!teste) return null

  const questoes = await questoesService.getByTeste(testeId)

  const questoesCompletas = await Promise.all(
    questoes.map(async (questao) => {
      const alternativas = await alternativasService.getByQuestao(questao.id)
      return {
        ...questao,
        alternativas
      }
    })
  )

  return {
    ...teste,
    questoes: questoesCompletas
  }
}

// Buscar resultado completo com respostas
export const getResultadoCompleto = async (resultadoId: string) => {
  const resultado = await resultadosService.getById(resultadoId)
  if (!resultado) return null

  const respostas = await respostasService.getByResultado(resultadoId)

  return {
    ...resultado,
    respostas
  }
}

// Estatísticas do dashboard
export const getDashboardStats = async () => {
  try {
    const [profiles, aulas, testes, questoes] = await Promise.all([
      profilesService.getAll(),
      aulasService.getAll(),
      testesService.getAll(),
      supabase.from('questoes').select('*')
    ])

    // Buscar todos os resultados diretamente
    const { data: allResultados } = await supabase.from('resultados').select('*')

    return {
      totalUsers: profiles.length,
      totalProfessores: profiles.filter(p => p.role === 'professor').length,
      totalAlunos: profiles.filter(p => p.role === 'aluno').length,
      totalAdmins: profiles.filter(p => p.role === 'admin').length,
      totalAulas: aulas.length,
      totalTestes: testes.length,
      totalQuestoes: questoes.data?.length || 0,
      totalTestesPublicados: testes.filter(t => t.is_published).length,
      totalResultados: allResultados?.length || 0,
      completedTests: allResultados?.length || 0 // Simplificado por enquanto
    }
  } catch (error) {
    console.error('Erro ao buscar estatísticas do dashboard:', error)
    throw error
  }
}

// ============================================================================
// TURMAS
// ============================================================================

export type Turma = Database['public']['Tables']['turmas']['Row']
export type TurmaInsert = Database['public']['Tables']['turmas']['Insert']
export type TurmaUpdate = Database['public']['Tables']['turmas']['Update']
export type TurmaAluno = Database['public']['Tables']['turma_alunos']['Row']
export type TurmaTeste = Database['public']['Tables']['turma_testes']['Row']
export type TurmaAula = Database['public']['Tables']['turma_aulas']['Row']

export const turmasService = {
  // Listar todas as turmas (professor vê criadas, aluno vê inscritas?)
  // Por enquanto, listar todas para admin/professor
  async getAll(): Promise<Turma[]> {
    const { data, error } = await supabase
      .from('turmas')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  },

  // Buscar turma por ID
  async getById(id: string): Promise<Turma | null> {
    const { data, error } = await supabase
      .from('turmas')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw error
    }
    return data
  },

  // Criar turma
  async create(turma: TurmaInsert): Promise<Turma> {
    const { data, error } = await supabase
      .from('turmas')
      .insert(turma as any)
      .select()
      .single()

    if (error) throw error
    return data as Turma
  },

  // Atualizar turma
  async update(id: string, updates: TurmaUpdate): Promise<Turma> {
    const { data, error } = await (supabase as any)
      .from('turmas')
      .update({ ...updates, updated_at: new Date().toISOString() } as any)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Turma
  },

  // Deletar turma
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('turmas')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // === Alunos ===
  async getAlunos(turmaId: string) {
    const { data, error } = await supabase
      .from('turma_alunos')
      .select('*, aluno:profiles(*)')
      .eq('turma_id', turmaId)

    if (error) throw error
    return data || []
  },

  async addAluno(turmaId: string, alunoId: string, email?: string) {
    const { error } = await (supabase as any)
      .from('turma_alunos')
      .insert({ turma_id: turmaId, aluno_id: alunoId, email } as any)

    if (error) throw error
  },

  async removeAluno(turmaId: string, alunoId: string) {
    const { error } = await supabase
      .from('turma_alunos')
      .delete()
      .eq('turma_id', turmaId)
      .eq('aluno_id', alunoId)

    if (error) throw error
  },

  // === Testes ===
  async getTestes(turmaId: string) {
    const { data, error } = await supabase
      .from('turma_testes')
      .select('*, teste:testes(*)')
      .eq('turma_id', turmaId)

    if (error) throw error
    return data || []
  },

  async addTeste(turmaId: string, testeId: string) {
    const { error } = await (supabase as any)
      .from('turma_testes')
      .insert({ turma_id: turmaId, teste_id: testeId } as any)

    if (error) throw error
  },

  async removeTeste(turmaId: string, testeId: string) {
    const { error } = await supabase
      .from('turma_testes')
      .delete()
      .eq('turma_id', turmaId)
      .eq('teste_id', testeId)

    if (error) throw error
  },

  // === Aulas ===
  async getAulas(turmaId: string) {
    const { data, error } = await supabase
      .from('turma_aulas')
      .select('*, aula:aulas(*)')
      .eq('turma_id', turmaId)

    if (error) throw error
    return data || []
  },

  async addAula(turmaId: string, aulaId: string) {
    const { error } = await (supabase as any)
      .from('turma_aulas')
      .insert({ turma_id: turmaId, aula_id: aulaId } as any)

    if (error) throw error
  },

  async removeAula(turmaId: string, aulaId: string) {
    const { error } = await supabase
      .from('turma_aulas')
      .delete()
      .eq('turma_id', turmaId)
      .eq('aula_id', aulaId)

    if (error) throw error
  },

  // === Helpers for Student View ===
  async getMyTurmas(alunoId: string) {
    const { data, error } = await supabase
      .from('turma_alunos')
      .select('turma:turmas(*)')
      .eq('aluno_id', alunoId)

    if (error) throw error
    return data.map((d: any) => d.turma) || []
  },

  async getMyTestes(alunoId: string) {
    // Complex query: Testes in Turmas where I am a member
    const { data, error } = await supabase
      .from('turma_alunos')
      .select(`
            turma_id,
            turma:turmas (
                turma_testes (
                    teste:testes (*)
                )
            )
          `)
      .eq('aluno_id', alunoId)

    if (error) throw error

    // Flatten the structure
    const testes: any[] = []
    data?.forEach((ta: any) => {
      ta.turma?.turma_testes?.forEach((tt: any) => {
        if (tt.teste) testes.push(tt.teste)
      })
    })

    // Remove duplicates
    const uniqueTestes = Array.from(new Map(testes.map(t => [t.id, t])).values())

    return uniqueTestes
  },

  async getMyAulas(alunoId: string) {
    const { data, error } = await supabase
      .from('turma_alunos')
      .select(`
            turma_id,
            turma:turmas (
                turma_aulas (
                    aula:aulas (*)
                )
            )
          `)
      .eq('aluno_id', alunoId)

    if (error) throw error

    // Flatten
    const aulas: any[] = []
    data?.forEach((ta: any) => {
      ta.turma?.turma_aulas?.forEach((tt: any) => {
        if (tt.aula) aulas.push(tt.aula)
      })
    })

    // Remove duplicates
    const uniqueAulas = Array.from(new Map(aulas.map(a => [a.id, a])).values())

    return uniqueAulas
  }

}

// ============================================================================
// TENTATIVAS
// ============================================================================

export const tentativasService = {
  // Arquivar uma tentativa (permitir retentativa)
  async archive(id: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('tentativas_testes')
      .update({ is_archived: true })
      .eq('id', id)

    if (error) throw error
  }
}